import React, { useContext, useEffect, useRef } from 'react';

import { ArrowRight } from 'lucide-react';

import { TintColorContext } from 'common/containers/TintColorContainer';
import { cn } from 'common/style/utils';
import Flex from 'common/ui/Flex';
import Spinner from 'common/uiV3/Spinner';

type LoadMoreV2Props = {
  className?: string;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  autoLoad?: boolean;
  label?: string;
};

const LoadMoreV2 = ({
  className,
  hasMore,
  loadingMore,
  onLoadMore,
  autoLoad = false,
  label = 'Load More',
}: LoadMoreV2Props) => {
  const tintColor = useContext(TintColorContext);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoLoad || !containerRef.current || !hasMore || loadingMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [autoLoad, hasMore, loadingMore, onLoadMore]);

  if (!hasMore) {
    return null;
  }

  if (loadingMore) {
    return (
      <Flex ref={containerRef} className="items-center justify-center py-4">
        <Spinner size="large" />
      </Flex>
    );
  }

  return (
    <div ref={containerRef} className={cn('flex items-center justify-center py-4', className)}>
      <Flex
        onClick={onLoadMore}
        className="w-full items-center justify-between hover:cursor-pointer hover:opacity-90"
        style={{ color: tintColor ?? undefined }}>
        {label}
        <ArrowRight className="size-5" />
      </Flex>
    </div>
  );
};

export default LoadMoreV2;
